import { Colors, Heading, SelectFilled, Text } from '@amelio/ui-library';

import { REPORT_PROFILE_SELECT_WIDTH, REPORT_TITLE, REPORT_TOOLBAR_LAYOUT } from '../reportStyles';

import type { SelectedFilters } from '@/hooks/filters/useCompanyAttributesFilters';
import type { CompetencyProfile } from '@/types/competency/CompetencyProfile';
import type { Company } from '@/types/directory/Company';
import type { OptionProps } from '@amelio/ui-library';

import {
    COMPANY_ATTRIBUTE_KINDS,
    CompanyAttributeFilters,
} from '@/components/CompanyAttributeFilters/CompanyAttributeFilters';
import { useAmelioTranslations } from '@/hooks';
import { getLocalizedText } from '@/utils/localization';

/** Sentinel option value for "every profile" — not a real profile id, so it never collides with
 * one, and maps to `competencyProfileId: undefined` on the report query. */
const ALL_PROFILES_VALUE = 'all';

interface ReportToolbarProps {
    company: Company | undefined;
    onFiltersChange: (filters: SelectedFilters) => void;
    profiles: CompetencyProfile[];
    selectedProfileId: string | undefined;
    onProfileChange: (profileId: string | undefined) => void;
}

/** Page title and subtitle, the seven-attribute company filter dropdown and the competency-profile
 * selector — everything above the summary cards and the matrix. */
export const ReportToolbar = ({
    company,
    onFiltersChange,
    profiles,
    selectedProfileId,
    onProfileChange,
}: ReportToolbarProps) => {
    const { t } = useAmelioTranslations();

    const profileOptions: OptionProps[] = [
        { value: ALL_PROFILES_VALUE, label: t('reports.competencyMatrix.profiles.all') },
        ...profiles.map((profile) => ({
            value: profile.id,
            label: getLocalizedText(profile.name),
        })),
    ];

    return (
        <div className={REPORT_TOOLBAR_LAYOUT}>
            <div className={'flex flex-col gap-1'}>
                <Heading
                    level={1}
                    color={Colors.NAVY}
                    className={REPORT_TITLE}
                >
                    {t('reports.competencyMatrix.title')}
                </Heading>

                <Text color={Colors.BASE_500}>{t('reports.competencyMatrix.subtitle')}</Text>
            </div>

            <div className={'flex flex-wrap items-start gap-3'}>
                <CompanyAttributeFilters
                    company={company}
                    onFiltersChange={onFiltersChange}
                    attributes={COMPANY_ATTRIBUTE_KINDS}
                />

                <div className={REPORT_PROFILE_SELECT_WIDTH}>
                    <SelectFilled
                        options={profileOptions}
                        value={selectedProfileId ?? ALL_PROFILES_VALUE}
                        onChange={(value) =>
                            onProfileChange(value === ALL_PROFILES_VALUE ? undefined : value)
                        }
                        placeholder={t('reports.competencyMatrix.profiles.all')}
                        dataTestId={'competency-matrix-profile-select'}
                    />
                </div>
            </div>
        </div>
    );
};
